import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FiscalProfileFormDialog, appliesTo } from './FiscalProfileFormDialog';
import {
  CUSTOMER_FISCAL_PROFILES_QUERY,
  REQUEST_CFDI_MUTATION,
  SAT_CATALOG_ENTRIES_QUERY,
} from './invoicing.gql';

import type { SatEntriesResponse } from './FiscalProfileFormDialog';
import type { FiscalProfile, Invoice } from './types';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/shared/ui/card';
import { Label } from '@/shared/ui/label';
import { Modal, ModalTitle } from '@/shared/ui/modal';

interface ProfilesResponse {
  customerFiscalProfiles: FiscalProfile[];
}

interface RequestCfdiDialogProps {
  sale_order_id: string;
  customer_id: string;
  on_close: () => void;
  on_done: (invoice: Invoice) => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

export function RequestCfdiDialog({
  sale_order_id,
  customer_id,
  on_close,
  on_done,
  on_error: on_parent_error,
}: RequestCfdiDialogProps): JSX.Element {
  const { t } = useTranslation();
  // Shown inside the modal: the parent's banner renders behind the overlay.
  const [error_text, setErrorText] = useState<string | null>(null);
  const on_error = (msg: string | null): void => {
    setErrorText(msg);
    on_parent_error(msg);
  };
  const [show_profile_form, setShowProfileForm] = useState(false);
  const {
    data,
    loading: loading_profiles,
    refetch: refetchProfiles,
  } = useQuery<ProfilesResponse>(
    CUSTOMER_FISCAL_PROFILES_QUERY,
    {
      variables: { customer_id },
      fetchPolicy: 'network-only',
    },
  );

  const profiles = (data?.customerFiscalProfiles ?? []).filter((p) => p.status === 'active');
  const default_profile = profiles.find((p) => p.is_default) ?? profiles[0];
  const [profile_id, setProfileId] = useState<string>(default_profile?.id ?? '');
  const [cfdi_usage_override, setCfdiUsageOverride] = useState<string>('');

  // Profiles arrive after the first render (network-only query), so the initial
  // state is empty; adopt the default once loaded unless the user already picked one.
  useEffect(() => {
    if (profile_id === '' && default_profile) setProfileId(default_profile.id);
  }, [profile_id, default_profile]);
  const [requestCfdi, { loading: submitting }] = useMutation(REQUEST_CFDI_MUTATION);

  const { data: usages_data } = useQuery<SatEntriesResponse>(SAT_CATALOG_ENTRIES_QUERY, {
    variables: { catalog: 'c_UsoCFDI', limit: 100 },
  });
  // 12-char RFC = persona moral, 13 = física; only offer usages valid for that type.
  const selected_profile = profiles.find((p) => p.id === profile_id);
  const rfc_len = selected_profile?.rfc.trim().length ?? 0;
  const person_known = rfc_len === 12 || rfc_len === 13;
  const usage_options = (usages_data?.satCatalogEntries ?? []).filter(
    (e) =>
      !person_known ||
      appliesTo(e, rfc_len === 12 ? 'aplicaParaTipoPersonaMoral' : 'aplicaParaTipoPersonaFisica'),
  );

  const can_submit = !submitting && profile_id.length > 0;

  // After creating a profile inline, reload the list and select the new one.
  const onDoneCreateProfile = async (): Promise<void> => {
    const known_ids = new Set(profiles.map((p) => p.id));
    setShowProfileForm(false);
    const result = await refetchProfiles();
    const created = (result.data?.customerFiscalProfiles ?? []).find(
      (p) => p.status === 'active' && !known_ids.has(p.id),
    );
    if (created) setProfileId(created.id);
  };

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    on_error(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const res = await requestCfdi({
        variables: {
          input: {
            sale_order_id,
            customer_fiscal_profile_id: profile_id,
            cfdi_usage: cfdi_usage_override.trim() || null,
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      const invoice = res.data?.requestCfdi as Invoice | undefined;
      if (invoice) await on_done(invoice);
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'invoice_already_stamped') return on_error(t('invoicing.error_already_stamped'));
        if (code === 'invoice_in_progress') return on_error(t('invoicing.error_in_progress'));
        if (code === 'invoicing_issuer_missing')
          return on_error(t('invoicing.error_issuer_missing'));
        if (code === 'invoice_requires_customer')
          return on_error(t('invoicing.error_requires_customer'));
        if (code === 'invoice_fiscal_profile_customer_mismatch')
          return on_error(t('invoicing.error_profile_mismatch'));
        if (code.startsWith('invoice_sale_not_invoiceable'))
          return on_error(t('invoicing.error_sale_not_invoiceable'));
        if (code.startsWith('pac_error'))
          return on_error(`${t('invoicing.error_pac')}: ${code.slice('pac_error:'.length)}`);
        return on_error(code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <Modal on_close={on_close} dismissible={!submitting} max_width="max-w-lg">
      <Card>
        <CardHeader>
          <ModalTitle>{t('invoicing.request_title')}</ModalTitle>
          <p className="text-xs text-muted-foreground">{t('invoicing.request_hint')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {error_text ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {error_text}
            </div>
          ) : null}
          {loading_profiles ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : profiles.length === 0 ? (
            <div className="space-y-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              <p>{t('invoicing.no_fiscal_profiles')}</p>
              <Button
                type="button"
                size="sm"
                onClick={() => setShowProfileForm(true)}
                disabled={submitting}
              >
                <Plus className="h-4 w-4" aria-hidden />
                <span>{t('invoicing.add_fiscal_profile')}</span>
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <Label>{t('invoicing.fiscal_profile')}</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={profile_id}
                  onChange={(e) => setProfileId(e.target.value)}
                  disabled={submitting}
                >
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label ? `${p.label} — ` : ''}
                      {p.legal_name} ({p.rfc})
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setShowProfileForm(true)}
                  disabled={submitting}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  <span>{t('invoicing.add_fiscal_profile')}</span>
                </Button>
              </div>
              <div className="space-y-1">
                <Label>{t('invoicing.cfdi_usage_override')}</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={cfdi_usage_override}
                  onChange={(e) => setCfdiUsageOverride(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">
                    {selected_profile
                      ? t('invoicing.cfdi_usage_profile_default', {
                          code: selected_profile.cfdi_usage,
                        })
                      : '—'}
                  </option>
                  {usage_options.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.code} - {o.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  {t('invoicing.cfdi_usage_override_hint')}
                </p>
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('invoicing.request_confirm')}
          </Button>
        </CardFooter>
      </Card>
      {show_profile_form ? (
        <FiscalProfileFormDialog
          customer_id={customer_id}
          profile={null}
          on_close={() => setShowProfileForm(false)}
          on_done={onDoneCreateProfile}
          // The form shows its own errors inside its modal.
          on_error={() => undefined}
        />
      ) : null}
    </Modal>
  );
}
